import { useState } from "react";
import { Loader2, RefreshCcw } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { rescoreCompany } from "@/api/companies";
import { cn } from "@/lib/utils";
import { scoreColor } from "@/lib/constants";

interface RescoreDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectSlug: string;
  companyId: number;
  currentScore: number | null;
  onSuccess: () => void;
}

export function RescoreDialog({
  open,
  onOpenChange,
  projectSlug,
  companyId,
  currentScore,
  onSuccess,
}: RescoreDialogProps) {
  const [submitting, setSubmitting] = useState(false);

  async function handleRescore() {
    setSubmitting(true);
    try {
      const updated = await rescoreCompany(projectSlug, companyId);
      const newScore = updated.score ?? 0;
      const delta = newScore - (currentScore ?? 0);
      toast.success(
        delta === 0
          ? `Score unchanged (${newScore})`
          : `Score updated: ${newScore} (${delta > 0 ? "+" : ""}${delta})`,
      );
      onSuccess();
      onOpenChange(false);
    } catch (err) {
      toast.error(extractErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[400px]">
        <DialogHeader className="items-center text-center">
          <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-indigo-50">
            <RefreshCcw className="h-4 w-4 text-indigo-600" />
          </div>
          <DialogTitle>Rescore this company</DialogTitle>
          <DialogDescription>
            The score will be recomputed against the latest company data and
            scoring rules.
          </DialogDescription>
        </DialogHeader>

        <div className="my-2 flex justify-center">
          <span
            className={cn(
              "inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1.5 text-sm font-medium",
            )}
          >
            <span className="text-xs uppercase tracking-wider text-slate-500">
              Current
            </span>
            <span className={scoreColor(currentScore)}>
              {currentScore ?? "—"}
              <span className="text-slate-400">/100</span>
            </span>
          </span>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleRescore}
            disabled={submitting}
          variant="primary"
          >
            {submitting && <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />}
            Recalculate
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function extractErrorMessage(err: unknown): string {
  if (axios.isAxiosError(err)) {
    const detail = (err.response?.data as { detail?: unknown } | undefined)
      ?.detail;
    if (typeof detail === "string") return detail;
  }
  return "Rescoring failed.";
}
